import { create } from 'zustand';
import { SessionMetadataApis } from '../apis/session-metadata/api';
import { resetAnalyticsUser, setAnalyticsBlocked, setTrackingContext } from '../services/analytics';

// One GET per session id, shared across every page in the guest exercise flow
// (/camera-permission, /try-demo, /exercise) that needs it for
// INPERFORMANCE_ACTION — each page calls fetchMetadata with
// whatever sessionId it already has, and this store dedupes repeat calls for
// the same session instead of refetching.
export const useSessionMetadataStore = create((set, get) => ({
  sessionId: null,
  metadata: null,
  // The camera page may offer its exercise-video toggle (the backend flag on).
  videoRecordingAvailable: false,
  status: 'idle', // idle | loading | loaded | error

  fetchMetadata: async sessionId => {
    if (!sessionId) {
      return;
    }
    const { sessionId: loadedSessionId, status } = get();
    if (loadedSessionId === sessionId && (status === 'loading' || status === 'loaded')) {
      return;
    }
    set({ sessionId, status: 'loading' });
    try {
      const response = await SessionMetadataApis.getSessionMetadata(sessionId);
      const metadata = response?.metadata ?? null;
      set({
        metadata,
        videoRecordingAvailable: response?.videoRecordingAvailable === true,
        status: 'loaded',
      });
      if (metadata) {
        // Apply analytics blocking before any identity or tracking calls
        const excluded = metadata.exclude_from_analytics === true;
        setAnalyticsBlocked(excluded);
        if (excluded) {
          resetAnalyticsUser();
          return;
        }
        // patient/therapist/prescription ride as Mixpanel super props (registered
        // once here) rather than being repeated on every trackEvent call below.
        setTrackingContext({
          patient: metadata.patient_id,
          therapist: metadata.therapist_id,
          prescription: metadata.prescription_id,
        });
      }
    } catch (_error) {
      set({ status: 'error' });
    }
  },
}));

// Explicit per-event properties — everything setTrackingContext above doesn't
// already cover as a super prop. Returns {} before metadata has loaded, so an
// event fired too early just ships action_type alone instead of throwing.
export function getInPerformanceTrackingProps() {
  const { metadata } = useSessionMetadataStore.getState();
  if (!metadata) {
    return {};
  }
  return {
    week_no: metadata.week_no,
    prescription_duration: metadata.prescription_duration,
    frequency_a_day: metadata.frequency_a_day,
    frequency_a_week: metadata.frequency_a_week,
    session_day_order: metadata.session_day_order,
    session_prescription_order: metadata.session_prescription_order,
    therapist_excluded: metadata.therapist_excluded,
    patient_excluded: metadata.patient_excluded,
  };
}
